import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  ChevronLeft,
  Copy,
  FilePlus2,
  FolderPlus,
  Loader2,
  Mic,
  MoreVertical,
  Save,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { hasRole } from "@/lib/roles";
import { LossReasonModal } from "@/components/LossReasonModal";
import ChantierAddrBlock from "@/components/quote/ChantierAddrBlock";
import AcompteEditor from "@/components/quote/AcompteEditor";
import TotalsPanel from "@/components/quote/TotalsPanel";
import SectionsList from "@/components/quote/SectionsList";
import ValidityCard from "@/components/quote/ValidityCard";
import StickyTotalsBar from "@/components/quote/StickyTotalsBar";
import SaveStatusBadge from "@/components/quote/SaveStatusBadge";
import DictateModal from "@/components/quote/DictateModal";
import RemindersWidget from "@/components/reminders/RemindersWidget";
import ChecklistPanel from "@/components/quote/ChecklistPanel";
import ClientPicker from "@/components/quote/ClientPicker";
import PhotoGallery from "@/components/quote/PhotoGallery";
import AddToCatalogModal from "@/components/quote/AddToCatalogModal";
import AssignSelector from "@/components/quote/AssignSelector";
import QuoteAssignedBadge from "@/components/quote/QuoteAssignedBadge";
// Lot P0 #1 (2026-09-09) — Bandeau readiness légale BE (VAT + IBAN).
// Affiché uniquement sur devis `signe` (info proactive avant émission facture).
import InvoiceReadinessBanner from "@/components/company/InvoiceReadinessBanner";
import {
  ExportExecutionBtn,
  PreviewPdfBtn,
  SendQuoteBtn,
} from "@/components/quote/QuoteActionButtons";
import useQuoteDraftAutosave from "@/hooks/useQuoteDraftAutosave";

function computePreview(lines) {
  let subtotal = 0;
  const byRate = {};
  let optionsHt = 0;
  let optionsTvac = 0;
  let hasCocontractant = false;
  for (const l of lines) {
    if (l.is_note) continue;
    if (l.vat_regime === "cocontractant" || l.vat_rate === 0) hasCocontractant = true;
    const ht = Number(l.quantity || 0) * Number(l.unit_price || 0);
    const tva = ht * (Number(l.vat_rate) / 100);
    if (l.is_option) {
      optionsHt += ht;
      optionsTvac += ht + tva;
      continue;
    }
    subtotal += ht;
    // Cocontractant: le taux 0% ne s'affiche pas comme un "TVA 0%" mais dans la ligne dédiée
    if (l.vat_regime !== "cocontractant" && l.vat_rate !== 0) {
      byRate[l.vat_rate] = (byRate[l.vat_rate] || 0) + tva;
    }
  }
  const tvac = subtotal + Object.values(byRate).reduce((a, b) => a + b, 0);
  const round = (x) => Math.round(x * 100) / 100;
  return {
    subtotal: round(subtotal),
    byRate: Object.fromEntries(Object.entries(byRate).map(([k, v]) => [k, round(v)])),
    tvac: round(tvac),
    optionsHt: round(optionsHt),
    optionsTvac: round(optionsTvac),
    hasCocontractant,
  };
}

// Phase RDV↔Devis (2026-09-09) — Lien inverse minimal fiche Devis → fiche projet
// (les RDV vivent dans la page /leads/{leadId}, pas de route /rendezvous/:id
// dédiée actuellement). Fetch la date + lead_id du RDV pour construire le lien.
// Fix B.2 (2026-09-09) — prop `inline` : rendu badge dans l'en-tête (à côté
// du badge Projet). Sans `inline` : rendu block legacy (non utilisé, kept
// for backward compat au cas où un autre composant l'appelle).
function LinkedRdvLine({ rdvId, inline = false }) {
  const [rdv, setRdv] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api
      .get(`/rendezvous/${rdvId}`)
      .then((res) => {
        if (cancelled) return;
        if (!res.data?.scheduled_at || !res.data?.lead_id) { setError(true); return; }
        setRdv(res.data);
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [rdvId]);
  if (error) {
    return (
      <div className={inline ? "text-xs italic text-slate-500" : "mt-2 text-xs italic text-slate-500"} data-testid="quote-linked-rdv-missing">
        (RDV lié introuvable)
      </div>
    );
  }
  const dateStr = rdv?.scheduled_at
    ? new Date(rdv.scheduled_at).toLocaleDateString("fr-BE", {
        day: "2-digit", month: "2-digit", year: "numeric",
      })
    : "…";
  if (inline) {
    return (
      <Link
        to={rdv ? `/leads/${rdv.lead_id}?rdv=${rdvId}` : "#"}
        className="inline-flex items-center gap-1 rounded border border-slate-600/60 bg-slate-800/40 px-2 py-0.5 text-xs text-slate-300 transition hover:border-[#E9A98F]/60 hover:bg-slate-700/60 hover:text-[#F5C4AC]"
        data-testid="quote-linked-rdv-link"
        title="Ouvrir le RDV d'origine"
      >
        Issu du RDV du <span className="font-medium">{dateStr}</span>
      </Link>
    );
  }
  return (
    <div className="mt-2 text-xs">
      Issu du RDV du{" "}
      <Link
        to={rdv ? `/leads/${rdv.lead_id}?rdv=${rdvId}` : "#"}
        className="text-[#E9A98F] underline underline-offset-2 hover:text-[#F5C4AC]"
        data-testid="quote-linked-rdv-link"
      >
        {dateStr}
      </Link>
    </div>
  );
}


export default function QuoteEditorPage() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { company, state: authState } = useAuth();
  const accentColor = company?.accent_color || "#C4553B";
  // Chantier 3 C9 (2026-09-06) — Un admin voit le dropdown "Assigné à",
  // les autres rôles voient uniquement un badge lecture seule (C10).
  const isAdmin = hasRole(authState?.user, ["admin"]);
  // Phase B rôle gestionnaire_chantier (2026-09-10) — Un gestionnaire_chantier ne peut
  // éditer/envoyer QUE ses propres avenants (`is_amendment=True` + créateur).
  // Le backend est source de vérité (403 sinon) ; on masque ici les boutons
  // pour aligner l'UI et éviter des toasts d'erreur inutiles.
  const isGestionnaireChantier = hasRole(authState?.user, ["gestionnaire_chantier"]);

  const [quote, setQuote] = useState(null);
  const [showRefuseModal, setShowRefuseModal] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [paymentSchedules, setPaymentSchedules] = useState([]);
  const [duplicating, setDuplicating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showChantierAddr, setShowChantierAddr] = useState(false);
  const [dictateOpen, setDictateOpen] = useState(false);
  const [addCatLine, setAddCatLine] = useState(null); // ligne à ajouter au catalogue (modal)
  const [missingChecklistIds, setMissingChecklistIds] = useState([]);
  const [chantierTypeId, setChantierTypeId] = useState(null);
  // Chantier 3 item 16 (2026-09-06) — Contexte projet (lead) pour badge cliquable
  const [projectLabel, setProjectLabel] = useState(null);
  // Phase 2C.M+ (2026-09-07) — État du sélecteur "Changer de client" sur brouillon.
  const [clientPickerOpen, setClientPickerOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [qRes, cRes, cs, psRes] = await Promise.all([
          api.get(`/quotes/${quoteId}`),
          api.get("/catalog").catch(() => ({ data: [] })),
          api.get("/company/settings"),
          api.get("/payment-schedules").catch(() => ({ data: [] })),
        ]);
        setQuote(qRes.data);
        setCatalog(cRes.data || []);
        setCompanySettings(cs.data);
        setPaymentSchedules(psRes.data || []);
        setShowChantierAddr(!!qRes.data.chantier_address);
        // Phase 4c — charge chantier_type_id pour le panneau checklist
        try {
          const ch = await api.get(`/chantiers/${qRes.data.chantier_id}`);
          setChantierTypeId(ch.data?.chantier_type_id || null);
        } catch {
          setChantierTypeId(null);
        }
        // Chantier 3 item 16 — charge le label du projet (lead) pour le badge
        try {
          const lead = await api.get(`/leads/${qRes.data.lead_id}`);
          const l = lead.data?.lead || lead.data;
          const nom = `${l?.first_name || ""} ${l?.last_name || ""}`.trim() ||
            l?.company_name || "Projet";
          setProjectLabel(nom);
        } catch {
          setProjectLabel(null);
        }
      } catch (e) {
        toast.error(formatApiError(e));
      }
    };
    load();
  }, [quoteId]);

  const isDraft = quote?.status === "brouillon";
  // Phase B lot correctifs (2026-09-10) — Doctrine RBAC "UI reflète le backend".
  // Un gestionnaire_chantier ne peut éditer QUE son propre avenant en brouillon
  // (miroir de `_forbid_if_gestionnaire_chantier_not_owner_amendment` backend).
  // `canEditQuote` remplace `isDraft` sur les CTA d'édition (Ajouter ligne,
  // Nouvelle section, Changer client, Supprimer section/devis, Dupliquer,
  // inputs de champs). Reste `isDraft` pour les mécaniques techniques
  // (autosave, `onBeforeAction`, PreviewPdf).
  const canEditAsGestionnaire =
    !isGestionnaireChantier ||
    (quote?.is_amendment === true &&
      quote?.created_by_user_id === authState?.user?.id);
  const canEditQuote = isDraft && canEditAsGestionnaire;
  // Suppression : miroir exact du gate backend (own amendment && brouillon).
  const canDeleteQuoteAsGestionnaire =
    quote?.is_amendment === true &&
    quote?.created_by_user_id === authState?.user?.id &&
    quote?.status === "brouillon";
  const canDeleteQuote = isDraft && (!isGestionnaireChantier || canDeleteQuoteAsGestionnaire);
  const sectionsOrder = quote?.sections_order?.length
    ? quote.sections_order
    : quote
      ? Array.from(new Set(quote.lines.map((l) => l.section || "Divers")))
      : [];

  // Autosave hook — TOUJOURS appelé (rules of hooks) ; guarde les cas quote=null en interne
  const {
    status: saveStatus,
    lastSavedAt,
    saveNow,
    isDirty,
  } = useQuoteDraftAutosave({
    quoteId,
    quote,
    sectionsOrder,
    showChantierAddr,
    isDraft: !!isDraft,
    onSaved: (data) => {
      setQuote(data);
      setShowChantierAddr(!!data.chantier_address);
    },
  });

  if (!quote) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const preview = computePreview(quote.lines || []);
  const client = quote.client_snapshot || {};

  // Phase 2C.M+ (2026-09-07) — Infobulle "Client figé" selon le statut.
  // Sur brouillon le bloc est éditable via ClientPicker. Sur envoyé/signé/refusé
  // /expiré, le client est figé (document contractuel envoyé au portail).
  const frozenTip = (() => {
    switch (quote.status) {
      case "envoye":
        return "Client figé : devis envoyé au portail public.";
      case "signe":
        return quote.signed_at
          ? `Client figé : devis signé le ${new Date(quote.signed_at).toLocaleDateString("fr-BE")}.`
          : "Client figé : devis signé.";
      case "refuse":
        return "Client figé : devis refusé.";
      case "expire":
        return "Devis expiré — dupliquez-le pour repartir sur une nouvelle offre.";
      default:
        return null;
    }
  })();

  const handleReassignLead = async (targetLeadId) => {
    try {
      const { data } = await api.patch(`/quotes/${quote.id}/reassign-lead`, {
        target_lead_id: targetLeadId,
      });
      setQuote(data);
      toast.success("Client mis à jour — lignes conservées");
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  // Phase 2A Item 3 (2026-09-03) — Détection des lignes totalement vides
  // (ni description, ni prix, ni quantité utile). Une ligne vide = artefact
  // d'insertion abandonnée. Signalée via bandeau non-bloquant + bouton purge.
  const emptyLineIds = (quote.lines || [])
    .filter(
      (l) =>
        !l.is_note &&
        !(l.description && l.description.trim()) &&
        (!l.unit_price || Number(l.unit_price) === 0) &&
        (!l.quantity || Number(l.quantity) === 0),
    )
    .map((l) => l.id);
  const purgeEmptyLines = () => {
    if (emptyLineIds.length === 0) return;
    const kept = (quote.lines || []).filter((l) => !emptyLineIds.includes(l.id));
    setLines(kept, sectionsOrder);
    toast.success(
      `${emptyLineIds.length} ligne${emptyLineIds.length > 1 ? "s" : ""} vide${emptyLineIds.length > 1 ? "s" : ""} supprimée${emptyLineIds.length > 1 ? "s" : ""}`,
    );
  };

  const linesBySection = sectionsOrder.reduce((acc, s) => {
    acc[s] = quote.lines
      .map((line, idx) => ({ line, idx }))
      .filter((x) => (x.line.section || "Divers") === s);
    return acc;
  }, {});

  const setLines = (lines, order) =>
    setQuote({ ...quote, lines, sections_order: order ?? quote.sections_order });
  const updateLine = (idx, patch) => {
    const lines = [...quote.lines];
    lines[idx] = { ...lines[idx], ...patch };
    setLines(lines);
  };
  const removeLine = (idx) => setLines(quote.lines.filter((_, i) => i !== idx));
  const reorderLinesInSection = (section, srcIdxInSec, dstIdxInSec) => {
    // Reconstruit la liste globale en réordonnant seulement les lignes de la section cible
    const secItems = linesBySection[section] || [];
    if (srcIdxInSec === dstIdxInSec || srcIdxInSec < 0 || dstIdxInSec < 0) return;
    const globalOrder = quote.lines.map((_, i) => i);
    const secGlobalIdx = secItems.map((it) => it.idx);
    // Retire l'index source de la position source, insère à la nouvelle
    const moved = secGlobalIdx.slice();
    const [pop] = moved.splice(srcIdxInSec, 1);
    moved.splice(dstIdxInSec, 0, pop);
    // Applique cet ordre dans globalOrder
    for (let i = 0, k = 0; i < globalOrder.length; i++) {
      if (secGlobalIdx.includes(globalOrder[i])) {
        globalOrder[i] = moved[k++];
      }
    }
    setLines(globalOrder.map((i) => quote.lines[i]));
  };
  const moveLineToSection = (sourceIdx, targetSection, position) => {
    const src = quote.lines[sourceIdx];
    if (!src) return;
    const remaining = quote.lines.filter((_, i) => i !== sourceIdx);
    const moved = { ...src, section: targetSection };
    if (position === "end" || typeof position !== "number") {
      // Append en fin de section cible : insère juste après la dernière ligne existante de cette section
      let lastIdx = -1;
      remaining.forEach((l, i) => {
        if ((l.section || "Divers") === targetSection) lastIdx = i;
      });
      const insertAt = lastIdx === -1 ? remaining.length : lastIdx + 1;
      remaining.splice(insertAt, 0, moved);
    } else {
      // position = index global de la ligne cible dans quote.lines original ; on l'ajuste
      const adjustedTarget = position > sourceIdx ? position - 1 : position;
      remaining.splice(adjustedTarget, 0, moved);
    }
    const order = sectionsOrder.includes(targetSection) ? sectionsOrder : [...sectionsOrder, targetSection];
    setLines(remaining, order);
  };
  const addLine = (section, extra = {}) => {
    // Hotfix Phase 1 (2026-09-02) — Zéro fallback silencieux. Si les settings
    // société ne sont pas encore chargés, on refuse l'ajout et on notifie l'utilisateur.
    // Le frontend affiche, il ne corrige pas. Voir DECISIONS.md § "TVA — source unique de vérité".
    if (!companySettings || companySettings.default_vat_rate == null) {
      toast.error("Paramètres société non chargés — impossible d'ajouter une ligne.");
      return;
    }
    const defaultVat = Number(companySettings.default_vat_rate);
    const defaultRegime = defaultVat === 0 ? "cocontractant" : "standard";
    const line = {
      description: "",
      quantity: 1,
      unit: "unité",
      unit_price: 0,
      vat_rate: defaultVat,
      vat_regime: defaultRegime,
      section,
      is_note: false,
      is_option: false,
      ...extra,
    };
    const lastIdx = [...quote.lines]
      .map((l, i) => ((l.section || "Divers") === section ? i : -1))
      .reduce((max, v) => (v > max ? v : max), -1);
    const lines = [...quote.lines];
    if (lastIdx === -1) {
      lines.push(line);
    } else {
      lines.splice(lastIdx + 1, 0, line);
    }
    const order = sectionsOrder.includes(section) ? sectionsOrder : [...sectionsOrder, section];
    setLines(lines, order);
  };
  const addSection = (forcedName) => {
    const name = forcedName ?? window.prompt("Nom de la nouvelle section", "");
    if (!name || !name.trim()) return;
    const clean = name.trim().slice(0, 80);
    if (sectionsOrder.includes(clean)) {
      toast.error("Cette section existe déjà");
      return;
    }
    setQuote({ ...quote, sections_order: [...sectionsOrder, clean] });
  };
  const renameSection = (oldName, newName) => {
    if (sectionsOrder.includes(newName)) {
      toast.error("Une section porte déjà ce nom");
      return;
    }
    const lines = quote.lines.map((l) =>
      (l.section || "Divers") === oldName ? { ...l, section: newName } : l,
    );
    const order = sectionsOrder.map((s) => (s === oldName ? newName : s));
    setLines(lines, order);
  };
  const removeSection = (name) => {
    const lines = quote.lines.filter((l) => (l.section || "Divers") !== name);
    const order = sectionsOrder.filter((s) => s !== name);
    setLines(lines, order);
  };
  const reorderSections = (newOrder) => {
    setQuote({ ...quote, sections_order: newOrder });
  };
  const addFromCatalog = (section, item) => {
    // Hotfix Phase 1 (2026-09-02) — Priorité au taux catalogue (item.default_vat_rate),
    // qui peut être 0 (cocontractant). Sinon fallback strict sur companySettings.
    // JAMAIS de valeur hardcodée. `??` (pas `||`) pour préserver 0.
    if (!companySettings || companySettings.default_vat_rate == null) {
      toast.error("Paramètres société non chargés — impossible d'ajouter depuis le catalogue.");
      return;
    }
    const itemVat = item.default_vat_rate;
    const vat = Number(
      itemVat != null && [0, 6, 12, 21].includes(Number(itemVat))
        ? itemVat
        : companySettings.default_vat_rate,
    );
    addLine(section, {
      description: item.pro_description?.trim() || item.label,
      unit: item.unit || "unité",
      unit_price: item.unit_price ?? 0,
      vat_rate: vat,
      vat_regime: vat === 0 ? "cocontractant" : "standard",
      catalog_item_id: item.id,
      section: item.section || section,
    });
  };

  // Phase 2A fix FAIL #1 (2026-09-02) — Insertion batch atomique depuis la modal
  // multi-select. Un seul setLines() sur un accumulateur local, sinon les appels
  // synchrones successifs de addFromCatalog liraient tous le même snapshot
  // `quote.lines` (closure) et le dernier écraserait tous les précédents.
  const addLinesFromCatalog = (section, items) => {
    if (!items || items.length === 0) return;
    if (!companySettings || companySettings.default_vat_rate == null) {
      toast.error(
        "Paramètres société non chargés — impossible d'ajouter depuis le catalogue.",
      );
      return;
    }
    const companyDefault = Number(companySettings.default_vat_rate);

    // Construit toutes les lignes à insérer en une passe
    const linesToInsert = items.map((item) => {
      const itemVat = item.default_vat_rate;
      const vat = Number(
        itemVat != null && [0, 6, 12, 21].includes(Number(itemVat))
          ? itemVat
          : companyDefault,
      );
      return {
        description: item.pro_description?.trim() || item.label,
        quantity: 1,
        unit: item.unit || "unité",
        unit_price: item.unit_price ?? 0,
        vat_rate: vat,
        vat_regime: vat === 0 ? "cocontractant" : "standard",
        section: item.section || section,
        catalog_item_id: item.id,
        is_note: false,
        is_option: false,
      };
    });

    // Regroupe par section cible (les items peuvent venir de sections catalogue variées)
    const currentLines = [...quote.lines];
    const targetSection = section;
    // Trouve l'index de la dernière ligne de la section cible pour insérer juste après.
    // NB : chaque item peut avoir sa propre section (item.section), mais on privilégie
    // la section active du menu (contrat produit : "insérer dans la section courante").
    const lastIdx = currentLines
      .map((l, i) => ((l.section || "Divers") === targetSection ? i : -1))
      .reduce((max, v) => (v > max ? v : max), -1);

    // Force toutes les nouvelles lignes dans la section cible pour cohérence UI
    const forceSectioned = linesToInsert.map((l) => ({ ...l, section: targetSection }));

    let newLines;
    if (lastIdx === -1) {
      newLines = [...currentLines, ...forceSectioned];
    } else {
      newLines = [
        ...currentLines.slice(0, lastIdx + 1),
        ...forceSectioned,
        ...currentLines.slice(lastIdx + 1),
      ];
    }
    const order = sectionsOrder.includes(targetSection)
      ? sectionsOrder
      : [...sectionsOrder, targetSection];
    setLines(newLines, order);
    toast.success(
      `${items.length} ligne${items.length > 1 ? "s" : ""} ajoutée${items.length > 1 ? "s" : ""} depuis le catalogue`,
    );
  };

  const save = async () => {
    try {
      const data = await saveNow();
      if (data) toast.success("Devis enregistré");
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      // Ne PAS rethrow : évite un unhandledrejection dans le bouton onClick.
      // L'état d'erreur est déjà positionné par le hook (badge → Erreur/Réessayer).
      return null;
    }
  };

  /**
   * Phase 4b — Injection des lignes générées par le modal d'assistant vocal.
   * Applique au state React puis force `saveNow()` explicite pour persister immédiatement
   * (respect règle DECISIONS.md § Phase 4b : `saveNow()` explicite après structuration).
   */
  const injectDictatedLines = async ({ lines, raw_notes }) => {
    if (!quote) return;
    // Hotfix Phase 1 (2026-09-02) — Si le backend renvoie un taux invalide (cas
    // impossible en pratique — le backend applique déjà `company_default_vat_rate`
    // via `_dictation_lines_to_quote_lines`), on fallback sur settings société,
    // JAMAIS sur une valeur hardcodée.
    const companyDefault = companySettings?.default_vat_rate;
    if (companyDefault == null) {
      toast.error("Paramètres société non chargés — impossible d'injecter la dictée.");
      return;
    }
    const newLines = (lines || []).map((l) => ({
      description: l.description || "",
      quantity: Number(l.quantity) || 0,
      unit: l.unit || "unité",
      unit_price: l.unit_price == null ? 0 : Number(l.unit_price),
      vat_rate: [0, 6, 12, 21].includes(Number(l.vat_rate))
        ? Number(l.vat_rate)
        : Number(companyDefault),
      vat_regime: l.vat_regime === "cocontractant" ? "cocontractant" : "standard",
      section: l.section || "Divers",
      catalog_item_id: l.catalog_item_id || null,
      needs_review: !!l.needs_review,
      review_reason: l.review_reason || null,
      is_note: false,
      is_option: false,
    }));
    // Merge notes de chantier au champ notes existant
    let mergedNotes = quote.notes || "";
    if (raw_notes && raw_notes.trim()) {
      const suffix = `--- Notes IA ---\n${raw_notes.trim()}`;
      mergedNotes = mergedNotes ? `${mergedNotes}\n\n${suffix}` : suffix;
    }
    setQuote((q) => ({
      ...q,
      lines: [...(q?.lines || []), ...newLines],
      notes: mergedNotes,
    }));
    // Le changement de state déclenche l'autosave via useQuoteDraftAutosave.
    // On force un saveNow() explicite pour immédiateté (aucune fenêtre d'attente).
    // Phase 2A Item 2 (2026-09-03) — Toast succès explicite : le user doit
    // voir que la dictée a bien été ajoutée au devis (append, jamais remplace).
    const injectedCount = newLines.length;
    if (injectedCount > 0) {
      toast.success(
        `${injectedCount} ligne${injectedCount > 1 ? "s" : ""} ajoutée${injectedCount > 1 ? "s" : ""} via dictée`,
      );
    }
    setTimeout(() => {
      saveNow().catch(() => {
        /* toast déjà géré par le hook / SaveStatusBadge */
      });
    }, 50);
  };

  const duplicate = async () => {
    setDuplicating(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/duplicate`);
      toast.success("Devis dupliqué");
      navigate(`/quotes/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDuplicating(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Supprimer ce brouillon ? Action irréversible.")) return;
    setDeleting(true);
    try {
      await api.delete(`/quotes/${quoteId}`);
      toast.success("Brouillon supprimé");
      navigate("/quotes");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  // Chantier 3 B7 (2026-09-06) — Autosave flush on-blur du champ actif.
  // Le hook `useQuoteDraftAutosave` a un debounce 1200 ms côté saisie. Un
  // utilisateur qui perd le focus (Tab, clic ailleurs, mobile keyboard down)
  // avant l'expiration du debounce risque de perdre sa saisie s'il ferme
  // l'onglet ou navigue rapidement. On force donc un `saveNow()` sur chaque
  // `blur` d'input/textarea/select DANS l'éditeur, dès que dirty.
  const handleBlurCapture = (event) => {
    if (!isDraft || !isDirty) return;
    const target = event.target;
    if (!target || !target.tagName) return;
    const tag = target.tagName.toLowerCase();
    if (tag !== "input" && tag !== "textarea" && tag !== "select") return;
    // Best-effort : les erreurs sont déjà remontées par le badge saveStatus
    saveNow().catch(() => {});
  };

  return (
    <div
      className="space-y-6 pb-24 qeditor:pb-0"
      data-testid="quote-editor-page"
      onBlurCapture={handleBlurCapture}
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
            data-testid="quote-back-btn"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold">
            Devis{" "}
            <span className="font-mono text-lg text-slate-400" data-testid="quote-number">
              {quote.number || "— brouillon —"}
            </span>
          </h1>
          <Badge className="border border-slate-600/60 bg-slate-700/30 text-slate-300" data-testid="quote-status-badge">
            {quote.status}
          </Badge>
          {/* Chantier 3 item 16 (2026-09-06) — Badge projet cliquable vers /leads/{id} */}
          {projectLabel && quote.lead_id && (
            <Link
              to={`/leads/${quote.lead_id}`}
              className="inline-flex items-center gap-1 rounded border border-slate-600/60 bg-slate-800/40 px-2 py-0.5 text-xs text-slate-300 transition hover:bg-slate-700/60 hover:text-slate-100"
              data-testid="quote-project-badge"
              title="Ouvrir la fiche projet"
            >
              <FolderPlus className="h-3 w-3" aria-hidden />
              Projet : <span className="font-medium">{projectLabel}</span>
            </Link>
          )}
          {/* Fix B.2 (2026-09-09) — Lien "Issu du RDV du…" déplacé du bloc
              client vers l'en-tête, à côté du badge Projet. Rationale
              sémantique : l'origine d'un devis n'est pas une coordonnée
              client mais une information de traçabilité qui doit
              cohabiter avec les autres badges de contexte de l'en-tête. */}
          {quote.rdv_id && <LinkedRdvLine rdvId={quote.rdv_id} inline />}
          {isDraft && (
            <SaveStatusBadge status={saveStatus} lastSavedAt={lastSavedAt} isDirty={isDirty} onRetry={save} />
          )}
          {/* Chantier 3 C9+C10 (2026-09-06) — Admin voit le dropdown, autres rôles le badge readonly */}
          {isAdmin ? (
            <AssignSelector
              quoteId={quote.id}
              currentUserId={quote.assigned_to}
              onChange={(newAssignedTo) => setQuote((q) => ({ ...q, assigned_to: newAssignedTo }))}
            />
          ) : (
            <QuoteAssignedBadge userId={quote.assigned_to} />
          )}
        </div>
        {/* Phase 10e-hotfix — Desktop actions (≥sm, comportement inchangé) */}
        <div className="hidden sm:flex flex-wrap items-center gap-2">
          {/* Phase B lot correctifs (2026-09-10) — Doctrine RBAC : masquer TOUS
              les CTA d'édition (édition, dupliquer, supprimer) si le user est
              gestionnaire ET le devis n'est pas un avenant à lui. */}
          {!canEditAsGestionnaire ? null : (
          <div className="contents">
          {isDraft && (
            <Button
              onClick={() => setDictateOpen(true)}
              variant="outline"
              className="border-[#C4553B]/40 bg-[#C4553B]/10 text-[#F0C572] hover:bg-[#C4553B]/20"
              data-testid="quote-dictate-btn"
            >
              <Mic className="mr-2 h-4 w-4" />
              Dicter
            </Button>
          )}
          {/* Phase 2A Item 1 (2026-09-03) — Bouton "Enregistrer" transformé en état
              textuel discret quand tout est saved (plus de bouton grisé mort).
              Réapparaît en bouton cliquable dès que dirty. */}
          {saveStatus === "saved" && !isDirty ? (
            <span
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-slate-400"
              data-testid="quote-save-state-saved"
              title={lastSavedAt ? `Enregistré à ${new Date(lastSavedAt).toLocaleTimeString("fr-BE", { hour: "2-digit", minute: "2-digit" })}` : "Enregistré"}
            >
              <Save className="h-3.5 w-3.5" />
              Enregistré{lastSavedAt ? ` · ${new Date(lastSavedAt).toLocaleTimeString("fr-BE", { hour: "2-digit", minute: "2-digit" })}` : ""}
            </span>
          ) : saveStatus === "error" ? (
            <Button
              onClick={save}
              className="bg-red-700 text-white hover:bg-red-600"
              data-testid="quote-save-btn"
            >
              <Save className="mr-2 h-4 w-4" />
              Réessayer l'enregistrement
            </Button>
          ) : (
            <Button
              onClick={save}
              disabled={saveStatus === "saving" || !isDraft}
              className="text-white disabled:opacity-50"
              style={{ backgroundColor: accentColor }}
              data-testid="quote-save-btn"
            >
              {saveStatus === "saving" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {saveStatus === "saving" ? "Enregistrement…" : "Enregistrer"}
            </Button>
          )}
          <Button onClick={duplicate} disabled={duplicating} variant="outline" className="border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-800" data-testid="quote-duplicate-btn">
            {duplicating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Copy className="mr-2 h-4 w-4" />}
            Dupliquer
          </Button>
          {canDeleteQuote && (
            <Button onClick={del} disabled={deleting} variant="outline" className="border-red-800/60 bg-red-950/30 text-red-300 hover:bg-red-950/50" data-testid="quote-delete-btn">
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Supprimer
            </Button>
          )}
          <SendQuoteBtn
            quoteId={quoteId}
            isDraft={canEditQuote}
            quote={quote}
            onSent={(fresh) => setQuote({ ...quote, ...fresh })}
            onBeforeAction={isDraft ? saveNow : undefined}
            onChecklistBlocked={(ids) => setMissingChecklistIds(ids)}
          />
          <PreviewPdfBtn quoteId={quoteId} disabled={false} onBeforeAction={isDraft ? saveNow : undefined} />
          <ExportExecutionBtn quoteId={quoteId} isDraft={isDraft} onBeforeAction={isDraft ? saveNow : undefined} />
          {quote.status === "envoye" && (
            <Button
              variant="outline"
              onClick={() => setShowRefuseModal(true)}
              className="border-[#A63D2F]/60 bg-[#A63D2F]/10 text-[#E67B6D] hover:bg-[#A63D2F]/20"
              data-testid="quote-refuse-btn"
            >
              Marquer refusé…
            </Button>
          )}
          </div>
          )}
          {/* Phase Avenants-α-1 (2026-09-10) — Créer un avenant sur un devis SIGNÉ.
              Doctrine RBAC : bouton avec sa propre garde autonome (miroir backend
              POST /quotes/{id}/amendments), placé HORS du wrapper `canEditAsGestionnaire`
              car un gestionnaire_chantier n'édite pas le parent signé mais peut créer
              son propre avenant. Dette mobile connue : entrée absente du menu
              `quote-actions-menu-content` — cf. groupe_d_backlog.md (α-2). */}
          {quote.status === "signe" && !quote.is_amendment && hasRole(authState?.user, ["admin", "gestionnaire_chantier"]) && (
            <Button
              onClick={async () => {
                try {
                  const { data } = await api.post(`/quotes/${quote.id}/amendments`);
                  toast.success(`Avenant ${data.number} créé`);
                  navigate(`/quotes/${data.id}`);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Erreur création avenant");
                }
              }}
              variant="outline"
              className="border-[#C4553B]/50 bg-[#C4553B]/10 text-[#F0C572] hover:bg-[#C4553B]/20"
              data-testid="create-amendment-btn"
            >
              <FilePlus2 className="mr-2 h-4 w-4" />
              Créer un avenant
            </Button>
          )}
        </div>

        {/* Phase 10e-hotfix — Mobile actions (<sm) : Save + menu overflow ⋯ + 3 composants complexes en flex-wrap */}
        <div className="flex w-full flex-wrap items-center gap-2 sm:hidden" data-testid="quote-actions-mobile">
          {/* Phase 2A Item 1 (2026-09-03) — Symétrie mobile : bouton devient badge textuel quand saved */}
          {saveStatus === "saved" && !isDirty ? (
            <span
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-2.5 py-2 text-xs text-slate-400"
              data-testid="quote-save-state-saved-mobile"
            >
              <Save className="h-3.5 w-3.5" />
              Enregistré{lastSavedAt ? ` · ${new Date(lastSavedAt).toLocaleTimeString("fr-BE", { hour: "2-digit", minute: "2-digit" })}` : ""}
            </span>
          ) : (
            <Button
              onClick={save}
              disabled={saveStatus === "saving" || !isDraft}
              className="flex-1 text-white disabled:opacity-50"
              style={{ backgroundColor: accentColor }}
              data-testid="quote-save-btn-mobile"
            >
              {saveStatus === "saving" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {saveStatus === "saving" ? "Enregistrement…" : "Enregistrer"}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-800"
                aria-label="Plus d'actions"
                data-testid="quote-actions-menu-btn"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="border-slate-700 bg-slate-900 text-slate-100"
              data-testid="quote-actions-menu-content"
            >
              {/* Phase B lot correctifs (2026-09-10) — Doctrine RBAC : items
                  d'édition masqués si gestionnaire ET pas propriétaire d'avenant */}
              {canEditAsGestionnaire && (
              <>
              {isDraft && (
                <DropdownMenuItem
                  onClick={() => setDictateOpen(true)}
                  className="focus:bg-[#C4553B]/20"
                  data-testid="quote-dictate-menuitem"
                >
                  <Mic className="mr-2 h-4 w-4 text-[#F0C572]" /> Dicter
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={duplicate}
                disabled={duplicating}
                data-testid="quote-duplicate-menuitem"
              >
                {duplicating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Copy className="mr-2 h-4 w-4" />}
                Dupliquer
              </DropdownMenuItem>
              {quote.status === "envoye" && (
                <DropdownMenuItem
                  onClick={() => setShowRefuseModal(true)}
                  className="text-[#E67B6D] focus:bg-[#A63D2F]/20 focus:text-[#E67B6D]"
                  data-testid="quote-refuse-menuitem"
                >
                  Marquer refusé…
                </DropdownMenuItem>
              )}
              {canDeleteQuote && (
                <>
                  <DropdownMenuSeparator className="bg-slate-800" />
                  <DropdownMenuItem
                    onClick={del}
                    disabled={deleting}
                    className="text-red-300 focus:bg-red-950/50 focus:text-red-200"
                    data-testid="quote-delete-menuitem"
                  >
                    {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                    Supprimer
                  </DropdownMenuItem>
                </>
              )}
              </>
              )}
              {/* Phase Avenants-α-2 (2026-09-11) — Ferme dette Doctrine #1
                  loggée en α-1 : menu mobile n'avait pas de bouton "Créer un
                  avenant". Placé HORS du wrapper canEditAsGestionnaire (miroir
                  exact du geste desktop) car un gestionnaire_chantier n'édite
                  pas le parent signé mais peut créer son propre avenant. */}
              {quote.status === "signe" && !quote.is_amendment && hasRole(authState?.user, ["admin", "gestionnaire_chantier"]) && (
                <>
                  <DropdownMenuSeparator className="bg-slate-800" />
                  <DropdownMenuItem
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/quotes/${quote.id}/amendments`);
                        toast.success(`Avenant ${data.number} créé`);
                        navigate(`/quotes/${data.id}`);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Erreur création avenant");
                      }
                    }}
                    className="text-[#F0C572] focus:bg-[#C4553B]/20 focus:text-[#F0C572]"
                    data-testid="create-amendment-menuitem"
                  >
                    <FilePlus2 className="mr-2 h-4 w-4" />
                    Créer un avenant
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Composants complexes (Send/Preview/Export) : rendus en flex-wrap sous ligne 1 sur mobile.
              Bouton natif de chaque composant, aucune duplication logique. */}
          <div className="flex w-full flex-wrap items-center gap-2 pt-1">
            <SendQuoteBtn
              quoteId={quoteId}
              isDraft={canEditQuote}
              quote={quote}
              onSent={(fresh) => setQuote({ ...quote, ...fresh })}
              onBeforeAction={isDraft ? saveNow : undefined}
              onChecklistBlocked={(ids) => setMissingChecklistIds(ids)}
            />
            <PreviewPdfBtn quoteId={quoteId} disabled={false} onBeforeAction={isDraft ? saveNow : undefined} />
            <ExportExecutionBtn quoteId={quoteId} isDraft={isDraft} onBeforeAction={isDraft ? saveNow : undefined} />
          </div>
        </div>
      </div>

      <LossReasonModal
        open={showRefuseModal}
        endpoint={`/quotes/${quoteId}/refuse`}
        successMessage="Devis marqué comme refusé"
        leadId={quote.lead_id}
        leadName={quote.number || "Devis"}
        onDone={() => {
          setShowRefuseModal(false);
          // Recharge le devis pour refléter le nouveau statut
          api.get(`/quotes/${quoteId}`).then((r) => setQuote(r.data)).catch(() => {});
        }}
        onCancel={() => setShowRefuseModal(false)}
      />

      {!isDraft && (
        <div className="rounded border border-[#D99A2B]/40 bg-[#D99A2B]/10 p-3 text-sm text-[#F0C572]">
          Ce devis n&apos;est plus un brouillon (statut : {quote.status}). Il est en lecture seule.
        </div>
      )}

      {/* Lot P0 #1 (2026-09-09) — Bandeau readiness légale BE (VAT + IBAN)
          affiché uniquement sur devis SIGNÉ : à ce stade, la facturation
          est imminente et le commercial doit savoir proactivement si
          l'entreprise a des champs légaux manquants/invalides. Le blocage
          réel (bouton disabled) se trouve dans `ChantierInvoicesSection`
          où l'émission facture est initiée. */}
      {quote.status === "signe" && (
        <InvoiceReadinessBanner />
      )}

      {/* Row 1 : Titre + adresse chantier | Client — layout 2/3 + 1/3 uniquement à partir de qeditor (≥1440) */}
      <div className="grid grid-cols-1 gap-4 qeditor:grid-cols-3">
        <Card className="border-slate-800 bg-slate-900/40 qeditor:col-span-2">
          <CardContent className="space-y-4 p-4">
            <div>
              <Label className="text-slate-300">Intitulé du devis</Label>
              <Input
                value={quote.title || ""}
                onChange={(e) => setQuote({ ...quote, title: e.target.value })}
                disabled={!isDraft}
                placeholder="Ex : Rénovation toiture principale avec isolation 120mm"
                className="mt-1 border-slate-700 bg-slate-950/40 text-slate-100"
                data-testid="quote-title-input"
                maxLength={200}
              />
              <p className="mt-1 text-xs text-slate-500">
                Obligatoire à l&apos;envoi. Aide le client à identifier son offre.
              </p>
            </div>
            <ChantierAddrBlock
              quote={quote}
              setQuote={setQuote}
              isDraft={canEditQuote}
              showChantierAddr={showChantierAddr}
              setShowChantierAddr={setShowChantierAddr}
            />
          </CardContent>
        </Card>

        <Card className="border-slate-800 bg-slate-900/40" data-testid="quote-client-block">
          <CardContent className="space-y-1 p-4">
            <div className="flex items-center justify-between">
              <h3
                className="text-xs uppercase tracking-wider text-slate-500"
                title={frozenTip || undefined}
                data-testid="quote-client-label"
              >
                {isDraft ? "Client" : "Client (figé)"}
              </h3>
              {canEditQuote ? (
                <button
                  type="button"
                  onClick={() => setClientPickerOpen(true)}
                  className="rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-[11px] font-medium text-slate-300 hover:border-[#C4553B] hover:text-[#E9A98F]"
                  data-testid="quote-change-client-btn"
                >
                  Changer de client
                </button>
              ) : frozenTip ? (
                <span
                  className="text-[10px] italic text-slate-500"
                  title={frozenTip}
                  data-testid="quote-client-frozen-tip"
                >
                  ⓘ
                </span>
              ) : null}
            </div>
            <div className="text-sm font-semibold text-slate-100">
              {`${client.first_name || ""} ${client.last_name || ""}`.trim() || "—"}
            </div>
            {client.email && <div className="text-xs text-slate-400">{client.email}</div>}
            {client.phone && <div className="text-xs text-slate-400">{client.phone}</div>}
            {(client.address_street || client.address_city) && (
              <div className="text-xs text-slate-500">
                {[client.address_street, client.address_zip, client.address_city].filter(Boolean).join(" ")}
              </div>
            )}
            {/* Phase RDV↔Devis (2026-09-09) — Lien "Issu du RDV du…" déplacé
                vers l'en-tête (fix B.2 2026-09-09). Voir <LinkedRdvLine>
                rendu en tête à côté de quote-project-badge. */}
            {/* Phase 2A (2026-09-02) — Lien "Voir le chantier" affiché UNIQUEMENT si un
                chantier est réellement lié. Évite un lien mort tant que le devis
                n'est pas signé (le chantier est créé automatiquement à la signature). */}
            {quote.chantier_id && (
              <Link
                to={`/chantiers/${quote.chantier_id}`}
                className="mt-2 inline-block text-xs text-[#E9A98F] hover:underline"
                data-testid="quote-chantier-link"
              >
                Voir le chantier →
              </Link>
            )}
          </CardContent>
        </Card>

        {/* Phase 2C.M+ (2026-09-07) — Sélecteur "Changer de client" sur brouillon uniquement */}
        <ClientPicker
          open={clientPickerOpen}
          onOpenChange={setClientPickerOpen}
          title="Changer de client"
          description="Lignes, sections et échéancier sont CONSERVÉS. Seul le destinataire du devis change. Uniquement disponible sur un devis brouillon."
          confirmLabel="Réaffecter"
          submittingText="Mise à jour…"
          sourceLeadId={quote.lead_id}
          onSelect={handleReassignLead}
        />
      </div>

      {/* Row 2 (< qeditor uniquement) : Validité + Échéancier en cards horizontales au-dessus des lignes */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 qeditor:hidden">
        <ValidityCard quote={quote} setQuote={setQuote} isDraft={canEditQuote} />
        <AcompteEditor
          quote={quote}
          setQuote={setQuote}
          isDraft={canEditQuote}
          companySettings={companySettings}
          paymentSchedules={paymentSchedules}
        />
      </div>

      {/* Row 3 : Éditeur lignes par sections + Panneau latéral (qeditor+ uniquement) */}
      <div className="grid grid-cols-1 gap-4 qeditor:grid-cols-3">
        <div className="qeditor:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-200">Lignes du devis</h3>
            {canEditQuote && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => addSection()}
                className="border-slate-700 bg-slate-800/60 text-slate-200"
                data-testid="add-section-btn"
              >
                <FolderPlus className="mr-1.5 h-3.5 w-3.5" /> Nouvelle section
              </Button>
            )}
          </div>

          {/* Phase 2A Item 3 (2026-09-03) — Bandeau lignes vides. Non-bloquant :
              informe l'user et propose une purge en un clic. */}
          {isDraft && emptyLineIds.length > 0 && (
            <div
              className="mb-3 flex flex-col gap-2 rounded-md border border-amber-900/40 bg-amber-950/20 px-3 py-2 text-sm text-amber-200 sm:flex-row sm:items-center sm:justify-between"
              data-testid="empty-lines-banner"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                <span>
                  {emptyLineIds.length} ligne{emptyLineIds.length > 1 ? "s" : ""} vide
                  {emptyLineIds.length > 1 ? "s" : ""} détectée{emptyLineIds.length > 1 ? "s" : ""}
                  {" "}(sans description ni prix ni quantité). À compléter ou supprimer avant envoi.
                </span>
              </div>
              <Button
                size="sm"
                onClick={purgeEmptyLines}
                variant="outline"
                className="shrink-0 border-amber-800/60 bg-amber-950/40 text-amber-100 hover:bg-amber-900/60"
                data-testid="empty-lines-purge-btn"
              >
                Supprimer les lignes vides
              </Button>
            </div>
          )}

          <SectionsList
            quote={quote}
            setQuote={setQuote}
            sectionsOrder={sectionsOrder}
            linesBySection={linesBySection}
            catalog={catalog}
            accentColor={accentColor}
            isDraft={canEditQuote}
            onAddSection={addSection}
            onAddLine={addLine}
            onUpdateLine={updateLine}
            onRemoveLine={removeLine}
            onRenameSection={renameSection}
            onRemoveSection={removeSection}
            onReorderSections={reorderSections}
            onReorderLines={reorderLinesInSection}
            onMoveLineToSection={moveLineToSection}
            onAddFromCatalog={addFromCatalog}
            onAddLinesFromCatalog={addLinesFromCatalog}
            onAddToCatalog={(line) => setAddCatLine(line)}
          />

          {/* Phase 4c — Galerie photos du chantier */}
          <div className="mt-4">
            <PhotoGallery quote={quote} setQuote={setQuote} isDraft={canEditQuote} />
          </div>

          <div className="mt-4">
            <Label className="text-slate-300">Notes / conditions particulières</Label>
            <Textarea
              rows={3}
              value={quote.notes || ""}
              onChange={(e) => setQuote({ ...quote, notes: e.target.value })}
              disabled={!isDraft}
              placeholder="Précisions techniques, contraintes de chantier, garanties spécifiques…"
              className="mt-1 border-slate-700 bg-slate-950/40 text-slate-100"
              data-testid="quote-notes"
            />
          </div>
        </div>

        {/* Panneaux latéraux — qeditor+ (≥1440) uniquement ; sur < qeditor, Validité + Acompte sont en cards horizontales en haut, Totaux en sticky bar en bas */}
        <div className="hidden space-y-4 qeditor:block">
          <ValidityCard quote={quote} setQuote={setQuote} isDraft={canEditQuote} />

          <AcompteEditor
            quote={quote}
            setQuote={setQuote}
            isDraft={canEditQuote}
            companySettings={companySettings}
            paymentSchedules={paymentSchedules}
          />

          {/* Phase Rentab-3 (13/09/2026) — Estimation interne (admin only).
              Info privée de rentabilité, JAMAIS exposée au client dans le PDF.
              Copiée vers le chantier lié au signage (set-if-none pour parent,
              additive pour avenant). */}
          {isAdmin && (
            <div
              className="rounded border border-slate-800 bg-slate-900/40 p-4"
              data-testid="quote-rentab-estimates-card"
            >
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-slate-200">
                  Estimation interne (rentabilité)
                </h3>
                <p className="mt-0.5 text-[11px] text-slate-500">
                  Admin uniquement. Non visible dans le PDF client. Copié
                  vers le chantier au signage.
                </p>
              </div>
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-[11px] uppercase tracking-wide text-slate-400">
                    Jours main-d&apos;œuvre estimés
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    inputMode="decimal"
                    disabled={!canEditQuote}
                    value={quote?.labor_days_estimated ?? ""}
                    onChange={(e) =>
                      setQuote({
                        ...quote,
                        labor_days_estimated: e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                    placeholder="—"
                    className="w-full rounded border border-slate-700 bg-slate-950/40 px-2 py-1.5 text-sm text-slate-100 tabular-nums focus:border-[#C4553B] focus:outline-none disabled:opacity-60"
                    data-testid="quote-labor-days-estimated"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] uppercase tracking-wide text-slate-400">
                    Sous-traitance estimée HTVA (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    disabled={!canEditQuote}
                    value={quote?.subcontracting_estimated_htva ?? ""}
                    onChange={(e) =>
                      setQuote({
                        ...quote,
                        subcontracting_estimated_htva: e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                    placeholder="—"
                    className="w-full rounded border border-slate-700 bg-slate-950/40 px-2 py-1.5 text-sm text-slate-100 tabular-nums focus:border-[#C4553B] focus:outline-none disabled:opacity-60"
                    data-testid="quote-subcontracting-estimated-htva"
                  />
                </div>
                {quote?.is_amendment && (
                  <p
                    className="rounded border border-amber-500/30 bg-amber-500/5 p-2 text-[10px] text-amber-200"
                    data-testid="quote-rentab-amendment-hint"
                  >
                    Avenant : ces valeurs seront <b>additionnées</b> à l&apos;estimation
                    du chantier existant lors du signage.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Phase 4c — Panneau checklist chantier_type (masqué si aucun) */}
          <ChecklistPanel
            quote={quote}
            setQuote={setQuote}
            isDraft={canEditQuote}
            chantierTypeId={chantierTypeId}
            highlightMissing={missingChecklistIds}
            onAddLine={async (item) => {
              // Ajoute une ligne libre au devis + retourne son id (attendu par ChecklistPanel).
              // Phase 4c iter29 fix : PATCH direct pour éviter la race condition avec l'autosave
              // hook (dont saveNow capture le state via closure et n'a pas encore vu la nouvelle
              // ligne au moment où on appelle après setQuote).
              // Hotfix Phase 1 (2026-09-02) — TVA depuis settings société. Zéro hardcode.
              if (!companySettings || companySettings.default_vat_rate == null) {
                toast.error(
                  "Paramètres société non chargés — impossible d'ajouter depuis la checklist.",
                );
                return null;
              }
              const vat = Number(companySettings.default_vat_rate);
              const newLineId = crypto.randomUUID();
              const newLine = {
                id: newLineId,
                description: item.label,
                quantity: 1,
                unit: "forfait",
                unit_price: 0,
                vat_rate: vat,
                vat_regime: vat === 0 ? "cocontractant" : "standard",
                section: "Divers",
                is_note: false,
                is_option: false,
              };
              const newLines = [...((quote && quote.lines) || []), newLine];
              try {
                const { data } = await api.patch(`/quotes/${quoteId}`, { lines: newLines });
                setQuote(data);
                return newLineId;
              } catch (e) {
                toast.error(formatApiError(e));
                return null;
              }
            }}
          />

          <TotalsPanel preview={preview} accentColor={accentColor} />

          {/* Phase 7a — Widget Relances IA (visible dès que le devis n'est plus brouillon) */}
          {!isDraft && (
            <RemindersWidget targetType="quote" targetId={quoteId} />
          )}
        </div>
      </div>

      {/* Sticky bar totaux — < qeditor uniquement, toujours visible pendant le scroll d'édition.
          qeditor:hidden appliqué directement sur StickyTotalsBar pour préserver le contexte fixed.
          Sur ≥ qeditor (1440), TotalsPanel dans la sidebar prend le relais. */}
      <StickyTotalsBar preview={preview} accentColor={accentColor} className="qeditor:hidden" />

      {/* Phase 4b — Modal assistant vocal enrichi */}
      <DictateModal
        quoteId={quoteId}
        open={dictateOpen}
        onClose={() => setDictateOpen(false)}
        onInject={injectDictatedLines}
      />

      {/* Phase 4c — Modal + Ajouter au catalogue depuis ligne hors-catalogue */}
      <AddToCatalogModal
        open={!!addCatLine}
        line={addCatLine}
        companyDefaultVat={companySettings?.default_vat_rate}
        onClose={() => setAddCatLine(null)}
        onCreated={(newCatalogItem) => {
          // Re-marque la ligne du devis avec catalog_item_id → plus de badge "Hors catalogue"
          if (!addCatLine) return;
          setQuote((q) => ({
            ...q,
            lines: (q?.lines || []).map((l) =>
              l.id === addCatLine.id ? { ...l, catalog_item_id: newCatalogItem.id } : l,
            ),
          }));
          setCatalog((c) => [...c, newCatalogItem]);
          setTimeout(() => {
            saveNow().catch((err) => {
              console.warn("[QuoteEditor] Post-catalog-add saveNow failed:", err?.message || err);
            });
          }, 50);
        }}
      />
    </div>
  );
}
